import {
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import { ReactElement, useEffect, useRef, useState } from "react";
import {
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";

import { FlexBox } from "~frontend/components/FlexBox";
import theme from "~frontend/theme";

export function RhfCurrencyField<T extends FieldValues>(
  props: Omit<TextFieldProps, "value" | "onChange" | "name"> &
    UseControllerProps<T> & {
      optional?: boolean;
      bottomLabel?: string;
      adornmentOffset?: number;
    }
): ReactElement {
  const { field, fieldState } = useController(props);

  const { optional, bottomLabel, ...rest } = props;

  const [displayValue, setDisplayValue] = useState(() =>
    field.value ? (field.value / 100).toFixed(2) : ""
  );

  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current) {
      setDisplayValue(field.value ? (field.value / 100).toFixed(2) : "");
    }
  }, [field.value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setDisplayValue(inputValue);

    if (inputValue === "") {
      field.onChange("");
      return;
    }

    const dollarAmount = parseFloat(inputValue);
    if (!isNaN(dollarAmount)) {
      const centsAmount = Math.round(dollarAmount * 100);
      field.onChange(centsAmount);
    }
  };

  const handleBlur = () => {
    isFocusedRef.current = false;

    if (displayValue && displayValue !== "") {
      const dollarAmount = parseFloat(displayValue);
      if (isNaN(dollarAmount)) {
        setDisplayValue("");
      } else {
        setDisplayValue(dollarAmount.toFixed(2));
      }
    }

    field.onBlur();
  };

  const handleFocus = () => {
    isFocusedRef.current = true;
  };

  return (
    <MuiTextField
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      value={displayValue}
      name={field.name}
      inputRef={field.ref}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error ? fieldState.error.message : bottomLabel}
      {...(rest as TextFieldProps)}
      label={
        <FlexBox alignItems="center" gap={1}>
          {props.label}
          {optional ? <Typography> (optional)</Typography> : ""}
        </FlexBox>
      }
      slotProps={{
        htmlInput: {
          ...rest.inputProps,
        },
        inputLabel: {
          sx: { width: "133%", maxWidth: `calc(140% - ${theme.spacing(4)})` },
          shrink: true,
          ...(props.InputLabelProps || props.slotProps?.inputLabel || {}),
        },
        input: {
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{
                marginLeft:
                  props.adornmentOffset !== undefined
                    ? props.adornmentOffset
                    : 1,
                marginRight:
                  props.adornmentOffset !== undefined
                    ? props.adornmentOffset * -1
                    : -1,
                marginTop: 0,
              }}
            >
              <Typography display="inline" sx={{ fontSize: "1.0em" }}>
                $
              </Typography>
            </InputAdornment>
          ),
          ...(props.InputProps || props.slotProps?.input || {}),
        },
      }}
    />
  );
}
